import type { ReactNode } from 'react';
import React from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { Maybe } from '@soundxyz/utils';
import { Indicator } from '@components/Notifications/shared';
import { AvatarUI } from '../avatar/Avatar';
import type { Pathname } from '../link/AccessibleLink';
import { AccessibleLink } from '../link/AccessibleLink';

const AVATAR_SIZE = 24;
const AVATAR_ACTIVE_SIZE = 20;

interface CommonNavLink {
  label: string;
  isActive?: boolean;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

interface NavLinkProps extends CommonNavLink {
  pathname: Pathname | null;
  icon: ReactNode;
  notificationIndicator?: boolean;
}

interface NavLinkAvatarProps extends CommonNavLink {
  avatarUrl: Maybe<string>;
  webappUri: Pathname;
  publicAddress: string;
  username: string;
}

export const MobileNavLinkV2 = (props: NavLinkProps) => {
  return (
    <AccessibleLink
      className="appearance-[button] flex-1 text-base900 no-underline active:scale-[0.98]"
      onClick={e => {
        e?.stopPropagation();
        props.onClick?.(e);
      }}
      pathnameUri={props.pathname || '/'}
    >
      <Container isActive={!!props.isActive} icon={props.icon} className="relative">
        {!!props.notificationIndicator && (
          <Indicator className="right-[calc(50%-15px)] top-0 h-2 w-2" />
        )}
        <span className="home-label font-base text-base-xs">{props.label}</span>
      </Container>
    </AccessibleLink>
  );
};

export const MobileNavLinkAvatarV2 = (props: NavLinkAvatarProps) => {
  return (
    <AccessibleLink
      className="appearance-[button] flex-1 no-underline active:scale-[0.98]"
      pathnameUri={props.webappUri}
      onClick={e => {
        e.stopPropagation();
        props.onClick?.(e);
      }}
    >
      <Container isActive={!!props.isActive}>
        <div className={clsx('h-[25px]', props.isActive ? 'pt-[2px]' : 'pt-[1px]')}>
          <AvatarUI
            size={props.isActive ? AVATAR_ACTIVE_SIZE : AVATAR_SIZE}
            src={props.avatarUrl}
            walletAddress={props.publicAddress}
            username={props.username}
            disableLink
            borderRadius={24}
            webappUriPath={props.webappUri}
            wrapperClassName={clsx(props.isActive && 'outline outline-2 !outline-offset-2')}
          />
        </div>
        <span className={clsx('home-label font-base text-base-xs')}>{props.label}</span>
      </Container>
    </AccessibleLink>
  );
};

const Container = ({
  children,
  className,
  isActive,
  icon,
}: {
  children: ReactNode;
  className?: string;
  isActive: boolean;
  icon?: React.ReactNode;
}) => {
  return (
    <div
      className={twMerge(
        'flex min-h-[43px] flex-col items-center justify-center gap-1',
        'data-[state=active]:text-base900 data-[state=inactive]:text-base600',
        'data-[state=active]:font-semibold data-[state=inactive]:font-medium',
        className,
      )}
      data-state={isActive ? 'active' : 'inactive'}
    >
      {!!icon && (
        <div className="flex h-[25px] w-[27px] flex-shrink-0 items-center justify-center [&_svg]:h-5">
          {icon}
        </div>
      )}
      {children}
    </div>
  );
};
